/**
 * Phone Module
 * International phone input initialization and handling
 */

/**
 * Interfaces for intl-tel-input (v17+)
 */
interface CountryData {
    name: string;
    iso2: string;
    dialCode: string;
}

interface IntlTelInputInstance {
    getSelectedCountryData(): CountryData;
    destroy(): void;
}

interface PhoneInputElement extends HTMLInputElement {
    phoneInputInstance?: IntlTelInputInstance;
}

declare const intlTelInput: (el: Element, options: object) => IntlTelInputInstance;
declare const intlTelInputGlobals: {
    getCountryData(): CountryData[];
};

const LANGUAGE_TO_COUNTRY: Record<string, string> = {
    'et': 'ee',
    'sv': 'se'
};

export function mapLocalizationToCountry(localization: string, countryData: CountryData[]): string {
    const lang = localization.toLowerCase();

    if (LANGUAGE_TO_COUNTRY[lang]) {
        return LANGUAGE_TO_COUNTRY[lang];
    }

    if (countryData.some((country) => { return country.iso2 === lang; })) {
        return lang;
    }

    return 'lv';
}

export function initializePhoneInput(form: HTMLFormElement, initialCountry: string): IntlTelInputInstance | null {
    const phoneInput = form.querySelector<PhoneInputElement>('.intl-tel-input');

    if (!phoneInput) {
        return null;
    }

    const phoneInstance = intlTelInput(phoneInput, {
        autoPlaceholder: 'aggressive',
        customContainer: 'intl-tel-input-wrapper',
        hiddenInput: 'phone',
        initialCountry: initialCountry,
        preferredCountries: [],
        separateDialCode: true,
        utilsScript: 'intlTelInputUtils.js',
    });

    phoneInput.phoneInputInstance = phoneInstance;

    let hiddenCountryInput = form.querySelector<HTMLInputElement>('input[name="phone_country"]');

    if (!hiddenCountryInput) {
        hiddenCountryInput = document.createElement('input');
        hiddenCountryInput.type = 'hidden';
        hiddenCountryInput.name = 'phone_country';
        form.appendChild(hiddenCountryInput);
    }

    hiddenCountryInput.value = initialCountry.toUpperCase();

    const validationFeedback = form.querySelector<HTMLElement>('.intl-tel-input-validation.invalid-feedback');

    if (validationFeedback && phoneInput.parentNode) {
        phoneInput.parentNode.insertBefore(validationFeedback, phoneInput.nextSibling);
    }

    return phoneInstance;
}

export function setupPhoneEvents(form: HTMLFormElement, phoneInstance: IntlTelInputInstance): void {
    const phoneInput = form.querySelector<HTMLInputElement>('.intl-tel-input');

    if (!phoneInput || !phoneInstance) {
        return;
    }

    phoneInput.addEventListener('keyup', (e: Event) => {
        const target = e.target as HTMLInputElement;
        target.value = target.value.replace(/[^0-9]+/g, '');
        refreshPhoneNumber(form);
    });

    phoneInput.addEventListener('paste', (e: Event) => {
        window.setTimeout(() => {
            stripCountryCodeFromInput(e.target as HTMLInputElement, phoneInstance);
            refreshPhoneNumber(form);
        }, 0);
    });

    phoneInput.addEventListener('countrychange', (e: Event) => {
        const target = e.target as HTMLInputElement;
        refreshPhoneNumber(form);
        updateCountryCode(form, phoneInstance);
        target.dispatchEvent(new Event('blur', { bubbles: true }));
    });
}

export function refreshPhoneNumber(form: HTMLFormElement): void {
    const dialCodeEl = form.querySelector<HTMLElement>('.iti__selected-dial-code');
    const phoneInput = form.querySelector<HTMLInputElement>('.intl-tel-input');
    const hiddenPhone = form.querySelector<HTMLInputElement>('input[name="phone"]');

    if (dialCodeEl && phoneInput && hiddenPhone) {
        const dialCode = dialCodeEl.textContent || '';
        const localNumber = phoneInput.value;
        hiddenPhone.value = dialCode + localNumber;
    }
}

export function updateCountryCode(form: HTMLFormElement, phoneInstance: IntlTelInputInstance | null): void {
    if (!phoneInstance) {
        return;
    }

    const countryData = phoneInstance.getSelectedCountryData();
    const countryInput = form.querySelector<HTMLInputElement>('input[name="phone_country"]');

    if (countryInput) {
        countryInput.value = countryData.iso2.toUpperCase();
    }
}

export function stripCountryCodeFromInput(input: HTMLInputElement, phoneInstance: IntlTelInputInstance | null): void {
    if (!phoneInstance) {
        return;
    }

    let value = input.value.replace(/[^0-9]+/g, '');
    const countryData = phoneInstance.getSelectedCountryData();
    const dialCode = countryData.dialCode;

    if (value.startsWith(dialCode)) {
        value = value.substring(dialCode.length);
        input.value = value;
    }
}

export class PhonePreparator {
    private readonly form: HTMLFormElement;
    private phoneInputInstance: IntlTelInputInstance | null = null;
    private readonly initialCountry: string;

    constructor(form: HTMLFormElement, options: { initialCountry?: string } = {}) {
        this.form = form;

        const countryData = typeof intlTelInputGlobals !== 'undefined'
            ? intlTelInputGlobals.getCountryData()
            : [];

        this.initialCountry = mapLocalizationToCountry(
            options.initialCountry || 'lv',
            countryData
        );
    }

    public initializeFields(): void {
        this.phoneInputInstance = initializePhoneInput(this.form, this.initialCountry);

        if (this.phoneInputInstance) {
            setupPhoneEvents(this.form, this.phoneInputInstance);
        }
    }
}

(window as unknown as { FormPreparator: typeof PhonePreparator }).FormPreparator = PhonePreparator;